import { useEffect, useState } from "react";
import {
  HERO_BUDGET_EVENT,
  startHeroBudgetMonitor,
  type BudgetEvaluation,
} from "@/lib/hero-perf-budgets";

/**
 * On-page hero performance budget warning.
 *
 * The monitor itself always runs (console warnings are free and useful in any
 * environment). The visible banner only renders in dev or when the page is
 * opened with `?heroBudget=1`, so real visitors never see QA chrome.
 */
export const HeroBudgetWarning = () => {
  const [evaluation, setEvaluation] = useState<BudgetEvaluation | null>(null);
  const [dismissed, setDismissed] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const forced = params.get("heroBudget");
    setVisible(forced === "1" || (import.meta.env.DEV && forced !== "0"));
  }, []);

  useEffect(() => {
    // Always monitor: console.warn fires regardless of banner visibility.
    const stop = startHeroBudgetMonitor();
    const onBudget = (event: Event) => {
      const detail = (event as CustomEvent<BudgetEvaluation>).detail;
      if (!detail?.violations?.length) return;
      setDismissed(false);
      setEvaluation(detail);
    };
    window.addEventListener(HERO_BUDGET_EVENT, onBudget as EventListener);
    return () => {
      stop();
      window.removeEventListener(HERO_BUDGET_EVENT, onBudget as EventListener);
    };
  }, []);

  if (!visible || dismissed || !evaluation?.violations.length) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      className="fixed bottom-4 left-4 z-[9999] max-w-sm rounded-lg border border-destructive/50 bg-card/95 p-4 text-card-foreground shadow-lg backdrop-blur"
    >
      <div className="flex items-start justify-between gap-3">
        <p className="text-sm font-semibold text-destructive">
          Hero perf budget exceeded ({evaluation.breakpoint} ·{" "}
          {evaluation.viewportWidth}px)
        </p>
        <button
          type="button"
          onClick={() => setDismissed(true)}
          aria-label="Dismiss hero performance warning"
          className="rounded px-1 text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          ×
        </button>
      </div>
      <ul className="mt-2 space-y-1 text-xs text-muted-foreground">
        {evaluation.violations.map((v) => (
          <li key={v.metric}>
            <span className="font-medium text-foreground">{v.label}</span>:{" "}
            {v.actualMs}ms vs {v.budgetMs}ms budget (+{v.overByMs}ms / +
            {v.overByPct}%)
          </li>
        ))}
      </ul>
      <p className="mt-2 text-[11px] text-muted-foreground">
        Tune budgets on <span className="font-mono">/qa/hero/perf</span> or via{" "}
        <span className="font-mono">?heroBudgetLcp=</span>.
      </p>
    </div>
  );
};

export default HeroBudgetWarning;
